import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Blog.css';
import { getLangFromPath, t, Lang } from '../utils/i18n';
import { getLocalizedBlogItems } from '../utils/blogData';

const Blog: React.FC = () => {
  const location = useLocation();
  const [lang, setLang] = useState<Lang>(getLangFromPath(location.pathname));
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    setLang(getLangFromPath(location.pathname));
  }, [location.pathname]);

  // Shared localized blog items list
  const items = getLocalizedBlogItems(lang as Lang);

  // Show all posts (no pagination cap)
  const postsPerPage = items.length;

  // Calculate pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = items.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(items.length / postsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleReadMore = (postId: number) => {
    navigate(`/${lang}/blog/${postId}`);
  };

  return (
    <div className="blog" style={{ overflowX: 'hidden' }}>
      {/* Hero Section - Matching Our Story Design */}
      <section className="blog-hero">
        <div className="container-grid">
          <div className="corners">
            <div className="text-content">
              <h2 className="title">{t(lang, 'blog.heroTitle')}</h2>
              <h3 className="subtitle">{t(lang, 'blog.heroSubtitle')}</h3>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <section className="news-section">
          <div className="news-grid">
            {currentPosts.map((post) => (
              <article key={post.id} className="news-card">
                <div className="news-image">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/news/news-placeholder.jpg';
                    }}
                  />
                </div>
                <div className="news-content">
                  <div className="news-meta">
                    <span className="news-category">{post.categoryText}</span>
                    <span className="news-date">{new Date(post.date).toLocaleDateString()}</span>
                  </div>
                  <h3 className="news-title">{post.title}</h3>
                  <p className="news-excerpt">{post.excerpt}</p>
                  <button 
                    className="read-more-btn"
                    onClick={() => handleReadMore(post.id)}
                  >
                    {t(lang as Lang, 'blog.readMoreBtn')}
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {t(lang as Lang, 'blog.prev')}
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {t(lang as Lang, 'blog.next')}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Blog; 